import { cx } from "@emotion/css";
import { useScrollTrigger } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import type { SearchCounts, SearchType } from "src/api/search";
import { MobileTopBar } from "src/components/GlobalSearch/MobileTopBar";
import { getActiveFilters } from "src/helpers/getActiveFilters";
import isMobile from "src/helpers/isMobile";
import {
	type CountDraft,
	useSearchDraftCount,
} from "src/hooks/useSearchDraftCount";
import { useServerSearch } from "src/hooks/useServerSearch";

import { ActiveFiltersChips } from "./Filters/ActiveFiltersChips";
import { FiltersDrawer } from "./Filters/FiltersDrawer";
import { SearchHeader } from "./SearchHeader";

const SHOW_AFTER_SCROLL_UP = 150;

type Details = {
	searchValue: string;
	questions?: unknown[];
	// treść pytania z globalnej szukajki — do chipa „Pytanie”, id nie pokazujemy
	questionLabel?: string;
	[key: string]: unknown;
};

type DefaultSearchProps = {
	details: Details;
	setDetails: (details: Details) => void;
	// clearFilters: fraza + wszystkie filtry (jak „Wyczyść” przy chipach)
	renderView: (params: {
		scrollToTop: () => void;
		clearFilters: () => void;
	}) => ReactNode;
	activeFilter: SearchType;
	isLoading?: boolean;
	// treść drawera filtrów (na szkicu); bez niej nie ma przycisku „Filtry”
	filters?: (
		draft: Details,
		setDraft: (draft: Record<string, unknown>) => void,
	) => ReactNode;
	// wartości domyślne filtrów — do chipów i czyszczenia
	defaultDetails?: Record<string, unknown>;
	// etykiety wartości filtrów z backendu (value → label) do chipów
	filterLabels?: Record<string, string>;
	count?: number;
	counts?: SearchCounts;
	// akcje widoku (sort, menu) w rzędzie tabów
	tabsActions?: ReactNode;
	// liczba wyników dla szkicu filtrów — na przycisk „Pokaż wyniki”; bez niej przycisk bez liczby
	countDraft?: CountDraft;
};

export function DefaultSearch({
	details,
	setDetails,
	renderView,
	activeFilter,
	isLoading,
	filters,
	defaultDetails = {},
	filterLabels,
	count,
	counts,
	tabsActions,
	countDraft,
}: DefaultSearchProps) {
	const { t } = useTranslation();
	const mobile = isMobile();
	const [scrollTarget, setScrollTarget] = useState<HTMLDivElement | null>(null);
	// belka nad przewiniętą treścią dostaje cień, żeby się nie zlewała
	// scroll w dół chowa taby i chipy, w górę pokazuje
	// pokazujemy dopiero po przewinięciu w górę o SHOW_AFTER_SCROLL_UP px — useScrollTrigger wracał od 1px
	const [isScrolledDown, setIsScrolledDown] = useState(false);
	useEffect(() => {
		if (!scrollTarget) return;
		let lastY = scrollTarget.scrollTop;
		let scrollUpDistance = 0;
		let collapsed = false;
		const handleScroll = () => {
			const y = scrollTarget.scrollTop;
			const delta = y - lastY;
			lastY = y;
			if (y <= 50) {
				scrollUpDistance = 0;
				collapsed = false;
			} else if (delta > 0) {
				scrollUpDistance = 0;
				collapsed = true;
			} else {
				scrollUpDistance -= delta;
				if (scrollUpDistance > SHOW_AFTER_SCROLL_UP) collapsed = false;
			}
			setIsScrolledDown(collapsed);
		};
		scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
		return () => scrollTarget.removeEventListener("scroll", handleScroll);
	}, [scrollTarget]);
	// ponytail: chowanie tylko na mobile — na desktopie zwolnione miejsce i tak nie było wykorzystane
	const isCollapsed = mobile && isScrolledDown;
	const [detailsHeight, setDetailsHeight] = useState(0);
	const isScrolled = useScrollTrigger({
		target: scrollTarget ?? undefined,
		disableHysteresis: true,
		threshold: 0,
	});
	const history = useHistory();
	// szkic filtrów w otwartym drawerze — request dopiero po „Pokaż wyniki”
	const [draft, setDraft] = useState<Details | null>(null);
	const activeFilters = filters
		? getActiveFilters(details, defaultDetails, t, filterLabels)
		: [];
	const draftFilters = draft
		? getActiveFilters(draft, defaultDetails, t, filterLabels)
		: [];
	const openFilters = () => setDraft(details);
	const { data: draftCount } = useSearchDraftCount(
		activeFilter,
		draft,
		countDraft,
	);

	const { searchValue, debouncedSearch, setSearchValue } = useServerSearch({
		initialValue: details.searchValue,
		debounceMs: 600,
	});

	// chip z frazą dopiero gdy wyszukanie poszło (nie w trakcie pisania)
	const phraseFilter =
		details.searchValue && details.searchValue === searchValue
			? [
					{
						key: "searchValue",
						label: t("search.filters.chips.searchValue", "Fraza"),
						values: [`„${details.searchValue}”`],
						reset: { searchValue: "" },
					},
				]
			: [];

	// pytanie wybrane w globalnej szukajce — chip jak fraza, nie w drawerze
	const questionFilter =
		details.questions?.length && details.questionLabel
			? [
					{
						key: "questions",
						label: t("search.filters.chips.question", "Pytanie"),
						values: [details.questionLabel],
						reset: { questions: [], questionLabel: undefined },
					},
				]
			: [];

	// filtry są inne na każdym typie, więc giną — fraza przechodzi dalej
	const changeType = (type: SearchType) =>
		history.push({
			pathname: generatePath(routing.paths.searchQuestion, {
				type: routing.searchTypes[type],
			}),
			state: { searchValue },
		});

	// biome-ignore lint/correctness/useExhaustiveDependencies: only react to debounced value changes
	useEffect(() => {
		if (debouncedSearch === details.searchValue) return;
		setDetails({ ...details, searchValue: debouncedSearch, questions: [] });
	}, [debouncedSearch]);

	const scrollToTop = () => {
		scrollTarget?.scrollTo({ top: 0, behavior: "smooth" });
	};

	const withoutFilters = (value: Details) => ({
		...value,
		...defaultDetails,
		searchValue: value.searchValue,
	});

	const clearFilters = () => {
		setSearchValue("");
		setDetails({ ...withoutFilters(details), searchValue: "" });
	};

	const applyDraft = () => {
		if (draft) setDetails(draft);
		setDraft(null);
	};

	// desktop: przewija się sam box wyników (belka stoi nad nim jak topbar), mobile: cały widok z belką sticky

	return (
		<div className="search-container">
			<div
				ref={mobile ? setScrollTarget : undefined}
				className="search-view-container"
			>
				{mobile && <MobileTopBar />}
				<div className="search-panel">
					<div
						className={cx("search-element-form", {
							"search-element-form--scrolled": isScrolled,
						})}
						// zwinięta belka = tyle samo miejsca w przepływie, inaczej lista skacze i scroll trigger się zapętla
						style={{
							marginBottom: isCollapsed ? detailsHeight : 0,
						}}
					>
						<SearchHeader
							collapsed={isCollapsed}
							onDetailsResize={setDetailsHeight}
							searchValue={searchValue}
							onSearchChange={setSearchValue}
							activeType={activeFilter}
							onTypeChange={changeType}
							isLoading={isLoading}
							count={count}
							counts={counts}
							tabsActions={tabsActions}
							activeFiltersCount={activeFilters.length}
							onOpenFilters={filters ? openFilters : undefined}
						>
							<ActiveFiltersChips
								filters={[...phraseFilter, ...questionFilter, ...activeFilters]}
								onEdit={(filter) =>
									!["searchValue", "questions"].includes(filter.key) &&
									openFilters()
								}
								onRemove={(filter) => {
									if (filter.key === "searchValue") setSearchValue("");
									setDetails({ ...details, ...filter.reset });
								}}
								onClearAll={clearFilters}
							/>
						</SearchHeader>
					</div>
					<div
						ref={mobile ? undefined : setScrollTarget}
						className="search-results"
					>
						{renderView({ scrollToTop, clearFilters })}
					</div>
				</div>
			</div>
			{filters && (
				<FiltersDrawer
					open={!!draft}
					onClose={() => setDraft(null)}
					onApply={applyDraft}
					onClear={() => draft && setDraft(withoutFilters(draft))}
					activeCount={draftFilters.length}
					resultsCount={draftCount}
				>
					{draft && filters(draft, (value) => setDraft(value as Details))}
				</FiltersDrawer>
			)}
		</div>
	);
}
